<template>
    <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
      <!-- Header -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div class="min-w-0">
            <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">User Management</h1>
            <p class="text-xs text-gray-500 dark:text-gray-400">Manage employees, roles, and access.</p>
          </div>
          <AddEmployee @toggle-message="EventMessage"></AddEmployee>
        </div>
        <div class="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div class="sm:flex-1">
            <v-text-field v-model="search" density="compact" variant="solo-filled" flat hide-details placeholder="Search users…" prepend-inner-icon="mdi-magnify"></v-text-field>
          </div>
          <input ref="csvInput" type="file" accept=".csv,text/csv" class="hidden" @change="onCsvSelected" />
          <button type="button" @click="downloadTemplate"
            class="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition active:scale-95 dark:border-gray-700 dark:text-gray-200">
            <v-icon size="16">mdi-file-download-outline</v-icon>Download template
          </button>
          <button type="button" @click="triggerCsvPicker" :disabled="importing"
            class="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition active:scale-95 disabled:opacity-60 dark:border-gray-700 dark:text-gray-200">
            <v-icon size="16" :class="importing ? 'animate-spin' : ''">{{ importing ? 'mdi-loading' : 'mdi-upload' }}</v-icon>Import CSV
          </button>
          <button type="button" @click="openTeamDlg"
            class="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition active:scale-95 dark:border-gray-700 dark:text-gray-200">
            <v-icon size="16">mdi-email-multiple-outline</v-icon>Daily email by team
          </button>
        </div>
      </div>

      <!-- User list -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div v-if="isloading" class="space-y-2">
          <div v-for="n in 6" :key="'usk-' + n" class="h-24 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"></div>
        </div>
        <template v-else>
          <EmptyState v-if="!employees.length" dense icon="mdi-account-search-outline" title="No users found" subtitle="Try adjusting your search." />
          <div v-else class="space-y-2">
            <div v-for="item in employees" :key="item.id"
              v-show="!search || (item.fullname + ' ' + item.email + ' ' + item.role + ' ' + item.team).toLowerCase().includes(search.toLowerCase())"
              class="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
              <div class="flex items-start justify-between gap-2">
                <div class="flex min-w-0 items-center gap-3">
                  <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">{{ initials(item.fullname) }}</span>
                  <div class="min-w-0">
                    <p class="truncate text-sm font-bold text-gray-900 dark:text-gray-50">{{ item.fullname }}</p>
                    <p class="truncate text-xs text-gray-500 dark:text-gray-400">{{ item.email }}</p>
                  </div>
                </div>
                <EditEmployee :user="item" :teams="teams" @toggle-message="EventMessage"></EditEmployee>
              </div>

              <div class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                <span v-if="item.role" class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 dark:bg-slate-500/15 dark:text-slate-300">{{ item.role }}</span>
                <span v-if="item.team" class="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">{{ item.team }}</span>
                <span class="inline-flex items-center gap-1"><v-icon size="13">mdi-calendar</v-icon>{{ item.created_at }}</span>
              </div>

              <div class="mt-2.5 flex items-center justify-between gap-2 border-t border-gray-100 pt-2 dark:border-gray-800">
                <div class="flex items-center gap-1.5">
                  <span class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Daily email</span>
                  <span class="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    :class="item.email_daily_tasks ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' : (item.daily_tasks_offered ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400')">
                    {{ item.email_daily_tasks ? (item.daily_tasks_time || '08:30').slice(0,5) : (item.daily_tasks_offered ? 'Offered' : 'Off') }}
                  </span>
                  <button type="button" aria-label="Edit daily email" @click="openEmailDlg(item)"
                    class="rounded-lg p-1 text-gray-500 transition hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"><v-icon size="16">mdi-email-clock-outline</v-icon></button>
                </div>
                <button type="button" @click="updateUser(item.active, item.id)" :disabled="loading"
                  class="rounded-lg px-3 py-1 text-xs font-semibold transition active:scale-95 disabled:opacity-60"
                  :class="item.active === 1 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'">
                  {{ item.active === 1 ? 'Active' : 'Inactive' }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
        <Snackbar  :toggle-snackbar="isshow" :response-message="message"></Snackbar>

        <!-- Per-user daily email settings -->
        <TaskEmailSettings v-model="emailDlg" :employee="emailTarget" @saved="onEmailSaved" @toggle-message="EventMessageOnly"></TaskEmailSettings>

        <!-- Apply daily email to a whole team/department -->
        <v-dialog v-model="teamDlg" max-width="460">
            <v-card prepend-icon="mdi-email-multiple-outline" title="Daily email by team">
                <v-card-text>
                    <v-select
                        v-model="teamForm.team"
                        :items="teams"
                        label="Team"
                        density="compact"
                        variant="outlined"
                        prepend-inner-icon="mdi-account-group-outline"
                    ></v-select>
                    <v-switch
                        v-model="teamForm.enabled"
                        color="primary"
                        density="compact"
                        hide-details
                        :label="teamForm.enabled ? 'Offer daily email to this team' : 'Turn off daily email for this team'"
                    ></v-switch>
                    <v-text-field
                        v-model="teamForm.time"
                        type="time"
                        label="Default send time (server time)"
                        density="compact"
                        variant="outlined"
                        prepend-inner-icon="mdi-clock-outline"
                        class="mt-3"
                        :disabled="!teamForm.enabled"
                    ></v-text-field>
                    <div class="text-caption text-grey">
                        <template v-if="teamForm.enabled">
                            Sets this as the default time and offers the daily email to all active members of the team.
                            Each member must switch it on themselves before they start receiving it.
                        </template>
                        <template v-else>
                            Turns the daily email off for all active members of the team.
                        </template>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text="Close" variant="plain" @click="teamDlg = false"></v-btn>
                    <v-btn color="primary" text="Apply" variant="flat" :loading="teamSaving" :disabled="!teamForm.team" @click="applyTeam()"></v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="importResultDialog" max-width="520">
            <v-card>
                <v-card-title class="text-h6">Import result</v-card-title>
                <v-card-text>
                    <div class="mb-2">{{ importResult.message }}</div>
                    <div v-if="importResult.errors && importResult.errors.length">
                        <div class="text-caption text-medium-emphasis mb-1">
                            Skipped / issues:
                        </div>
                        <ul class="pl-4">
                            <li
                                v-for="(err, i) in importResult.errors"
                                :key="i"
                                class="text-caption"
                            >
                                {{ err }}
                            </li>
                        </ul>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" variant="text" @click="importResultDialog = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
  import axios from 'axios';
  import exportCsv from '@/utils/exportCsv';
  import constant from '@/constant';
  import utils from '@/utils';
  import apiMixin from '@/mixins/apiMixin';
  import formatMixin from '@/mixins/formatMixin';
  import EmptyState from '@/components/ui/EmptyState.vue';
  import AddEmployee from './AddEmployee.vue';
  import EditEmployee from './EditEmployee.vue';
  import Snackbar from './Snackbar.vue';
  import TaskEmailSettings from './TaskEmailSettings.vue';
    export default {
        components: {
        AddEmployee,
        EditEmployee,
        Snackbar,
        EmptyState,
        TaskEmailSettings
        },
        mixins: [apiMixin, formatMixin],
    data() {
            return {
                loading: false,
                importing: false,
                importResultDialog: false,
                importResult: { message: "", errors: [] },
                search: "",
                isshow: false,
                message: "",
                isloading: true,
                constant: constant,
                headers: [
                { title: 'Fullname', key: 'fullname' },
                { title: 'Email', key: 'email' },
                { title: 'Role', key: 'role' },
                { title: 'Team', key: 'team' },
                { title: 'Date Created', key: 'created_at' },
                { title: 'Daily email', key: 'daily_email', sortable: false },
                { title: '', key: 'active' },
                { title: '', key: 'action', sortable: false },
                ],
                teams: [],
                employees: [
                ],
                emailDlg: false,
                emailTarget: null,
                teamDlg: false,
                teamSaving: false,
                teamForm: { team: null, enabled: true, time: '08:30' },
      }
    },
      methods: {
        EventMessage(value){
            this.getUsers();
            this.isshow = true;
            this.message = value;
            console.log("snackbar from users page", this.isshow);
            setTimeout(() => {
                    this.isshow = false;
            }, 1000);
        },
        // Toast without refetching (used by the email-settings dialog).
        EventMessageOnly(value){
            this.isshow = true;
            this.message = value;
            setTimeout(() => { this.isshow = false; }, 1500);
        },
        openEmailDlg(item){
            this.emailTarget = {
                id: item.id,
                fullname: item.fullname,
                email_daily_tasks: item.email_daily_tasks,
                daily_tasks_time: item.daily_tasks_time,
            };
            this.emailDlg = true;
        },
        onEmailSaved(payload){
            // Reflect the change on the row without a full refetch.
            const row = this.employees.find((e) => e.id === payload.id);
            if (row) {
                row.email_daily_tasks = payload.email_daily_tasks;
                row.daily_tasks_time = payload.daily_tasks_time;
                row.daily_tasks_offered = false;
            }
        },
        openTeamDlg(){
            this.teamForm = { team: null, enabled: true, time: '08:30' };
            this.teamDlg = true;
        },
        applyTeam(){
            if (!this.teamForm.team) return;
            this.teamSaving = true;
            const payload = {
                team: this.teamForm.team,
                email_daily_tasks: this.teamForm.enabled,
                daily_tasks_time: this.teamForm.time || '08:30',
            };
            axios.post(utils._api(this.constant.apply_task_email_team), payload, this.authHeaders())
                .then((response) => {
                    this.teamSaving = false;
                    this.teamDlg = false;
                    this.EventMessageOnly(response.data.message || 'Applied');
                    this.getUsers();
                })
                .catch((error) => {
                    this.teamSaving = false;
                    console.error('Error applying team email prefs:', error);
                    this.EventMessageOnly('Failed to apply to team');
                });
        },
        getTeams(){
           axios.get(utils._api(this.constant.get_teams),{
                headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'Authorization' : `Bearer ${localStorage.getItem('token')}`
                    },
            }).then((response) => {
                this.teams = (response.data.data || []).map((team) => team.team_name);
            }).catch(error => {
                console.error("Error fetching teams:", error);
            });
        },
        getUsers(){
           axios.get(utils._api(this.constant.get_users),{
                headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'Authorization' : `Bearer ${localStorage.getItem('token')}`
                    },
            }).then((response) => {
                // this.employees = response.data.data;
                this.employees = response.data.data.map((employee) => ({
                id: employee.id,
                company_id: employee.company_id,
                team: employee.team,
                fullname: employee.fullname,
                role: employee.role,
                email: employee.email,
                created_at:  this.formattedDateTime(employee.created_at),
                active: employee.active,
                email_daily_tasks: employee.email_daily_tasks,
                daily_tasks_time: employee.daily_tasks_time,
                daily_tasks_offered: employee.daily_tasks_offered,
                }));
                this.isloading = false;
                // this.teams = response.data.data;
                console.log('Teams', response.data);
            }).catch(error => {
                console.error("Error fetching teams:", error);
            });
        },
      downloadTemplate(){
            axios.get(utils._api(this.constant.users_template), this.authHeaders())
                .then((response) => { exportCsv('user_accounts_import_template.csv', response.data); })
                .catch((error) => { console.error('Error downloading template:', error); this.EventMessageOnly('Could not download the template'); });
        },
      triggerCsvPicker(){
            this.$refs.csvInput.click();
        },
        onCsvSelected(event){
            const file = event.target.files && event.target.files[0];
            if (!file) return;

            this.importing = true;
            const f = new FormData();
            f.append("file", file);

            axios.post(utils._api(this.constant.import_users), f, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization' : `Bearer ${localStorage.getItem('token')}`
                },
            }).then((response) => {
                this.importing = false;
                this.importResult = {
                    message: response.data.message,
                    errors: response.data.errors || []
                };
                this.importResultDialog = true;
                this.getUsers();
            }).catch(error => {
                this.importing = false;
                const data = error.response && error.response.data;
                this.importResult = {
                    message: (data && data.message) || "Import failed.",
                    errors: data && data.errors
                        ? Object.values(data.errors).flat()
                        : []
                };
                this.importResultDialog = true;
            }).finally(() => {
                // allow re-selecting the same file
                if (this.$refs.csvInput) this.$refs.csvInput.value = "";
            });
        },
      updateUser(active,employee_id){
            this.loading = true;
            const f = new FormData();
            f.append("employee_id", employee_id);
            f.append("active", !active);
           axios.post(utils._api(this.constant.update_user),f , {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Authorization' : `Bearer ${localStorage.getItem('token')}`
                },
            }).then((response) => {
                this.loading = false;
                this.isshow = !this.isshow;
                this.message = response.data.message;
                this.getUsers();
                console.log(this.message);
            });
        }
      },
        mounted(){
            this.getUsers();
            this.getTeams();
        },
        watch: {
        loading (val) {
            if (!val) return

            setTimeout(() => (this.loading = false), 2000)
        },
        },
    }
</script>